import { SortDirection } from '@stylehaven/components';

import { ExportEntity } from '../../entities/downloads/export-entity.entity';

const PART_NAME: RegExp = /^(\d{14})-(\d+)-van-(\d+)\.zip$/;

const KEY_STAMP: RegExp = /^(\d{4})(\d{2})(\d{2})(\d{2})(\d{2})(\d{2})$/;

export interface ParsedName {
    readonly key: string;
    readonly part: number;
    readonly total: number;
}

export interface GroupedPart {
    readonly part: number;
    readonly job: ExportEntity;
}

export type DownloadGrouping =
    | { readonly kind: 'group'; readonly key: string; readonly total: number; readonly parts: GroupedPart[] }
    | { readonly kind: 'flat'; readonly job: ExportEntity };

export function parseExportName(name: string): ParsedName | null {
    const match: RegExpExecArray | null = PART_NAME.exec(name ?? '');

    if (!match) {
        return null;
    }

    return { key: match[1], part: Number(match[2]), total: Number(match[3]) };
}

/**
 * The name a deel will carry once it exists, in the shape {@link parseExportName} reads back. The overview
 * needs it before the deel does: the list serves a deel only once the backend has made it, so a deel still
 * being made has no row of its own to take a title from.
 */
export function toPartName(key: string, part: number, total: number): string {
    return `${key}-${part}-van-${total}.zip`;
}

/**
 * When the export was asked for, in epoch milliseconds. The key is not an opaque id: it is the aanmaakmoment
 * as `yyyyMMddHHmmss`, which is what lets a group judge how old its gaps are while holding no deel that
 * carries a date. Read as UTC, the zone the CreationDate beside it is served in.
 *
 * `null` for a key of any other shape, so the caller decides what an unreadable one means rather than
 * inheriting an epoch that would read as ancient.
 */
export function parseKeyDate(key: string): number | null {
    const match: RegExpExecArray | null = KEY_STAMP.exec(key ?? '');

    if (!match) {
        return null;
    }

    const [, year, month, day, hour, minute, second] = match;

    return Date.UTC(Number(year), Number(month) - 1, Number(day), Number(hour), Number(minute), Number(second));
}

export function groupDownloads(jobs: ExportEntity[]): DownloadGrouping[] {
    const candidates: Map<string, GroupedPart[]> = new Map();

    (jobs ?? []).forEach(job => {
        const parsed: ParsedName | null = parseExportName(job.Name);

        if (parsed) {
            const members: GroupedPart[] = candidates.get(parsed.key) ?? [];
            members.push({ part: parsed.part, job });
            candidates.set(parsed.key, members);
        }
    });

    const groupable: Set<string> = new Set(
        [...candidates.entries()].filter(([, members]) => isGroupable(members)).map(([key]) => key)
    );

    const emitted: Set<string> = new Set();

    return (jobs ?? []).flatMap((job): DownloadGrouping[] => {
        const parsed: ParsedName | null = parseExportName(job.Name);

        if (!parsed || !groupable.has(parsed.key)) {
            return [{ kind: 'flat', job }];
        }

        if (emitted.has(parsed.key)) {
            return [];
        }
        emitted.add(parsed.key);

        const parts: GroupedPart[] = [...candidates.get(parsed.key)!].sort((a, b) => a.part - b.part);

        return [{ kind: 'group', key: parsed.key, total: parsed.total, parts }];
    });
}

function isGroupable(members: GroupedPart[]): boolean {
    const totals: Set<number> = new Set(members.map(member => parseExportName(member.job.Name)!.total));
    const partNumbers: Set<number> = new Set(members.map(member => member.part));

    return totals.size === 1 && partNumbers.size === members.length;
}

export function sortDownloadGroupings(
    groupings: DownloadGrouping[],
    direction: SortDirection
): DownloadGrouping[] {
    const sign: number = direction === SortDirection.ASC ? 1 : -1;

    return [...groupings].sort((a, b) => {
        const byDate: number = rankDate(a).localeCompare(rankDate(b));

        return byDate !== 0 ? sign * byDate : rankKey(a).localeCompare(rankKey(b));
    });
}

function rankDate(grouping: DownloadGrouping): string {
    if (grouping.kind === 'flat') {
        return grouping.job.CreationDate ?? '';
    }

    return grouping.parts
        .map(part => part.job.CreationDate ?? '')
        .filter(date => !!date)
        .sort()[0] ?? '';
}

function rankKey(grouping: DownloadGrouping): string {
    return grouping.kind === 'flat' ? grouping.job.ExportJobId : grouping.key;
}
